import React from 'react'
import { useState, useMemo, useRef, useContext, useEffect } from 'react'
import { useRouter } from 'next/router'
import _omit from 'lodash/omit'
import _pick from 'lodash/pick'
import _pickBy from 'lodash/pickBy'
import _isEmpty from 'lodash/isEmpty'
import _isEqual from 'lodash/isEqual'
import _groupBy from 'lodash/groupBy'
import _flatten from 'lodash/flatten'
import classNames from 'classnames'
import Button from 'component_library/1_Atoms/Button'
import Spinner from 'react-bootstrap/Spinner'
import { CANCELED_API_ERROR, PAGINATION_LIMIT } from 'constants/api'
import ListCard from 'app/ProviderSearch/ListCard'
import { RudderEvent } from 'constants/rudderEvents'
import {
  PRIMARY_FILTERS,
  SECONDARY_FILTERS,
  SLOT_LOCATION_MAP,
} from 'app/ProviderSearch/constants'
import { ProviderFilterTypes } from 'dataSource/ProviderSearch'
import {
  getGenderValuesFromIds,
  getSlotsStartEndDate,
  getFilterValueFromParsedURL,
  formatSupplyReserveParams,
  hasSecondaryFilters,
} from 'app/ProviderSearch/utils'
import qs from 'qs'
import {
  hasError,
  getParsedQuery,
  getTimeZoneFormattedDate,
  sortGeneratedSlots,
  modifyURLQuery,
} from 'utils/common'
import useApi from 'hooks/useApi'
import useToast from 'hooks/useToast'
import useBreakpoint from 'hooks/useBreakPoint'
import { GET_SLOTS, PROVIDERS } from 'dataSource/ProviderSearch/routes'
import { CachePolicies } from 'use-http'
import SlotDateContext from 'context/ProviderBooking/SlotDate'
import { TimeZoneContext } from 'context/common/TimeZoneContext'
import {
  sessionStorageKeys,
  dateFormats,
  SLOTS_ROW_LIMIT_V3,
} from 'constants/common'
import useThriveAuth from 'hooks/useThriveAuth'
import { TreatmentFlags, treatmentFlagValues } from 'constants/treatments'
import { getTreatment } from 'utils/treatment'
import { parseProviderDataWithSlots } from 'hooks/slots/helper'
import { callRudderEventForAvailableProviderShown } from 'app/ProviderSearch/rudderEventUtils'
import logEvent from 'utils/logEvent'
import styles from './index.module.scss'
import commonStyles from '../common.module.scss'
import { InsuranceProviderContext } from 'context/common/InsuranceProvider'

const ProvidersListWaterfall: React.FC<ProvidersListWatefallProps> = ({
  scrollContainer,
  groupedFilters,
  filterLookup,
  providerList,
  selectedPractice,
  inPersonFilterSelected,
  countDetails,
  onSetIsZeroProviderState,
  noWaterfallProviderAcceptsInsurance,
  setNoWaterfallProviderAcceptsInsurance,
}) => {
  const router = useRouter()
  const { query } = router
  const { state } = query
  const parsedQuery = getParsedQuery(router)
  const { isSupportOrThirdPartyUser, user } = useThriveAuth()
  const { isMobileScreen, isExtraLargeSize, isAboveXLSize } = useBreakpoint()
  const { showError } = useToast()
  const { businessPartner } = user ?? {}
  const [providerListLoading, setProviderListLoading] = useState<boolean>(true)
  const [waterfallResult, setWaterfallResult] = useState<
    FilterWaterfallProvider[]
  >([])
  const [showLoadMoreButton, setShowLoadMoreButton] = useState<boolean>(true)
  const { request: apiRequest } = useApi(null, null, CachePolicies.CACHE_FIRST)
  const previousQueryRef = useRef<string>('')
  const { timeZone } = useContext(TimeZoneContext)
  const focusProviderCardRef = useRef<HTMLDivElement>(null)
  const queryRef = useRef(parsedQuery)
  const filterIndex = useRef(0)
  const removedFilters = useRef([])
  const { startSlotDate, slotListLimit } = useContext(SlotDateContext)

  // refs for rudderstack event
  const countDetailsRef = useRef<{
    slot_sum: number
    provider_count: ProviderCount[]
    next_available_slot_sum: number
  }>({ slot_sum: 0, provider_count: [], next_available_slot_sum: 0 })
  // this is same as countDetailsRef the only differnce being we are not updating it's value
  // unlike countDetailsRef
  // and is used to capture updated value of parent's countDetails
  const parentCountDetailsRef = useRef<{
    slot_sum: number
    provider_count: ProviderCount[]
    next_available_slot_sum: number
  }>({ slot_sum: 0, provider_count: [], next_available_slot_sum: 0 })

  const bucketIteration = useRef(0)

  const isDesktop = isExtraLargeSize || isAboveXLSize

  const { insuranceProvider } = useContext(InsuranceProviderContext)

  const insuranceNameInSession = sessionStorage.getItem(
    sessionStorageKeys.INSURANCE_PROVIDER_NAME
  )

  // On initial load the insuranceProvider is null, so we need to get the insuranceProvider from query
  const formattedGroupedFilters = {
    ...groupedFilters,
    ...((insuranceProvider?.id || query?.insuranceId) && {
      [ProviderFilterTypes.INSURANCE_ID]: [
        {
          id: insuranceProvider?.id
            ? insuranceProvider.id
            : query?.insuranceId
            ? query.insuranceId
            : '',
          value: insuranceProvider?.name
            ? insuranceProvider.name
            : insuranceNameInSession
            ? insuranceNameInSession
            : '',
          type: ProviderFilterTypes.INSURANCE_ID,
        },
      ],
    }),
  }

  const getFiltersValues = (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    referenceURL: Record<string, any>,
    pickPath: string[]
  ) => {
    const filters = _pick(referenceURL, pickPath)
    const notNullFilters = _pickBy(filters, (value) => value)
    const notNullFiltersKeyValue = getFilterValueFromParsedURL(
      formattedGroupedFilters,
      notNullFilters
    )
    const notNullFiltersKeyValues = _flatten(
      Object.values(notNullFiltersKeyValue)
    )
    return notNullFiltersKeyValues
  }

  const previousSelectedSlot: SelectedSlot = useMemo(
    () =>
      JSON.parse(
        sessionStorage.getItem(sessionStorageKeys.PREVIOUS_SELECTED_SLOT)
      ),
    [waterfallResult]
  )

  const getCurrentWaterfallProviders = () => {
    const waterfallProviders = waterfallResult.reduce((acc, item) => {
      const providerDetailsArray = item.results.map(
        (result) => result.providerDetails
      )
      return [...acc, ...providerDetailsArray]
    }, [])
    return waterfallProviders
  }

  /**
   * Fetches providers with the selected filters.
   * And based on if filters is applied or not, either fetchPractices or fetchRestProvidersForPractice
   * Will be called to get the practices.
   */

  const fetchProviders = async (parsedQuery): Promise<void> => {
    try {
      if (!providerListLoading) {
        setProviderListLoading(true)
      }

      const { partnershipCode } = parsedQuery

      const { startDate, endDate } = getSlotsStartEndDate(
        startSlotDate,
        slotListLimit,
        timeZone
      )

      const isSupplyReserveFlagEnabled =
        getTreatment(TreatmentFlags.SUPPLY_RESERVE, {
          state,
        }) === treatmentFlagValues.ON
      const {
        id: b2bPartnerId,
        isActive: isB2bPartnerActive,
        partnershipCode: existingB2bPartnerKeyword,
      } = businessPartner ?? {}
      const isExistingB2bUser = isB2bPartnerActive && !!b2bPartnerId
      const b2bPartnershipCode = isExistingB2bUser
        ? existingB2bPartnerKeyword
        : partnershipCode

      const queryObj = {
        ..._pick(parsedQuery, [
          'state',
          'longitude',
          'latitude',
          'offset',
          'limit',
          'areaOfConcern',
          'insuranceId',
          'counselingType',
          'availabilityType',
          'orderBy',
          'gender',
          'clientAgeGroup',
          'language',
          'counselingApproach',
          'practiceId',
          'timeOfDay',
        ]),
        slotStartDate: startDate.toISOString(),
        slotEndDate: endDate.toISOString(),
        ...(isSupplyReserveFlagEnabled &&
          !!b2bPartnershipCode && { partnershipCode: b2bPartnershipCode }),
        ...(!!parsedQuery.timeOfDay && { timezone: timeZone }),
      }

      const qsQuery = qs.stringify(queryObj, {
        addQueryPrefix: true,
        skipNulls: true,
      })
      const requestUrl = `${PROVIDERS}${qsQuery}`

      const responseProviderList = await apiRequest.get(requestUrl)

      if (responseProviderList === undefined) {
        throw new Error(CANCELED_API_ERROR)
      }

      const {
        result: providerListResult,
        intakeSupplyReserveDetails,
        pagination: providerPagination,
      } = responseProviderList
      hasError(apiRequest, responseProviderList)

      //TODO: Check the usability of this
      providerListResult?.forEach((provider: ListCardProvider) => {
        const { providerSlugs } = provider.practice
        provider.practice.providerSlugs = providerSlugs.map((slug) => ({
          name: slug,
        }))
        provider.filters = provider.availabilityTypeIds?.reduce(
          (filters, id) => [...filters, filterLookup[id]?.[0]],
          []
        )
      })
      //--------------------------------------------

      const currentWaterfallProviders = getCurrentWaterfallProviders()
      const existingProviders = [...providerList, ...currentWaterfallProviders]
      const existingProvidersIds = existingProviders.map((item) => item.id)

      const recentNewProviders = providerListResult.filter(
        (item) => !existingProvidersIds.includes(item.id)
      )

      const supplyReserveParams = isSupplyReserveFlagEnabled
        ? formatSupplyReserveParams(intakeSupplyReserveDetails, user?.userType)
        : {}

      bucketIteration.current = bucketIteration.current + 1

      // if there are no new providers returned by the api, recall it

      if (recentNewProviders.length === 0) {
        loadMoreProviders()

        if (!isDesktop) {
          countDetailsRef.current.slot_sum =
            countDetailsRef.current.next_available_slot_sum
        }

        // call the rudder event if it's the first bucket case
        // adding timeout because countDetailsRef is not updated at this point
        if (
          bucketIteration.current === 1 &&
          !hasSecondaryFilters(parsedQuery)
        ) {
          setTimeout(() => {
            callRudderEventForAvailableProviderShown({
              providers: existingProviders,
              countDetails: countDetailsRef.current,
              totalProvidersAvailable: existingProviders.length,
              groupedFilters: formattedGroupedFilters,
              query,
              startSlotDate,
              selectedPractice,
              user,
              intakeSupplyReserveParams: supplyReserveParams,

              directProvidersCount: providerList.length,
              indirectProvidersCount: 0,
              directSlotsCountDetails: countDetailsRef.current,
              indirectSlotsCountDetails: { slot_sum: 0, provider_count: [] },
              timeZone,
            })
          }, 500)
        }
      } else {
        const totalProviders = [...existingProviders, ...recentNewProviders]

        const slots = await apiRequest.post(GET_SLOTS, {
          start: startDate,
          end: endDate,
          for: recentNewProviders.map((provider) => ({
            providerId: provider.id,
            practiceId: provider?.practice?.id,
          })),
          ...(parsedQuery.timeOfDay && {
            timeOfDay: parsedQuery.timeOfDay,
            timezone: timeZone,
          }),
        })

        const generatedProviderSlots = {}
        const nextAvailabilityMap: NextAvailabilityMap = {}

        const groupedSlots = _groupBy(slots, (slot) => slot.providerId)
        recentNewProviders?.map((provider) => {
          const providerSlots = groupedSlots[provider.id]

          if (providerSlots?.length) {
            const groupedDateSlots: DateSlot = _groupBy(
              providerSlots[0]?.slots,
              (slot) =>
                getTimeZoneFormattedDate(
                  slot.start,
                  dateFormats.YEAR_MONTH_DAY_FORMAT,
                  timeZone
                )
            )
            const sortedGroupedDateSlots = sortGeneratedSlots(groupedDateSlots)
            generatedProviderSlots[provider.id] = sortedGroupedDateSlots
            //creating next availability map for providers
            nextAvailabilityMap[provider.id] =
              providerSlots?.[0]?.nextAvailableSlot ?? null
          }
        })
        // -----------------------------------------------------
        const {
          providerCardDetails,
          countDetails: recentCountDetails,
        } = parseProviderDataWithSlots({
          providerSlots: generatedProviderSlots,
          providersList: recentNewProviders,
          nextAvailabilityForProviders: nextAvailabilityMap,
          startSlotDate,
          slotListLimit,
          counselingTypedFilters:
            formattedGroupedFilters[ProviderFilterTypes.COUNSELING_TYPE],
          slotsRowLimit: SLOTS_ROW_LIMIT_V3,
          error: null,
          areSlotsLoading: false,
          timeOfDay: parsedQuery.timeOfDay,
          ...(!!parsedQuery.timeOfDay && { timeZone }),
        })

        const waterfallData = [
          {
            filters: {
              appliedFilters: getFiltersValues(queryRef.current, [
                ...PRIMARY_FILTERS,
                ...SECONDARY_FILTERS,
              ]),
              removedFilters: removedFilters.current,
            },
            results: [...providerCardDetails],
            providerPagination,
          },
        ]

        setWaterfallResult((prevData) => [...prevData, ...waterfallData])

        // changes for rudderstack event
        countDetailsRef.current = {
          ...countDetailsRef.current,
          slot_sum:
            countDetailsRef.current?.slot_sum + recentCountDetails.slot_sum,
          provider_count: [
            ...countDetailsRef.current?.provider_count,
            ...recentCountDetails.provider_count,
          ],
          next_available_slot_sum:
            countDetailsRef.current?.next_available_slot_sum +
            recentCountDetails.next_available_slot_sum,
        }

        const parentCountDetailsProviderId = parentCountDetailsRef.current.provider_count.map(
          (provider) => Object.keys(provider)[0]
        )

        /**
         * Here we can't use countDetails instead of parentCountDetailsRef because
         * countDetails at this point is not updated on this scope
         */
        const indirectSlotsCountDetails = {
          slot_sum:
            countDetailsRef.current.slot_sum -
            parentCountDetailsRef.current.slot_sum,
          provider_count: countDetailsRef.current.provider_count.filter(
            (provider) =>
              !parentCountDetailsProviderId.includes(Object.keys(provider)[0])
          ),
          next_available_slot_sum:
            countDetailsRef.current.next_available_slot_sum -
            parentCountDetailsRef.current.next_available_slot_sum,
        }

        if (!isDesktop) {
          countDetailsRef.current.slot_sum =
            countDetailsRef.current.next_available_slot_sum
          indirectSlotsCountDetails.slot_sum =
            indirectSlotsCountDetails.next_available_slot_sum
          parentCountDetailsRef.current.slot_sum =
            parentCountDetailsRef.current.next_available_slot_sum
        }

        callRudderEventForAvailableProviderShown({
          providers: totalProviders,
          countDetails: countDetailsRef.current,
          totalProvidersAvailable: totalProviders.length,
          groupedFilters: formattedGroupedFilters,
          query,
          startSlotDate,
          selectedPractice,
          user,
          intakeSupplyReserveParams: supplyReserveParams,

          directProvidersCount: providerList.length,
          indirectProvidersCount: totalProviders.length - providerList.length,
          directSlotsCountDetails: parentCountDetailsRef.current,
          indirectSlotsCountDetails: indirectSlotsCountDetails,
          timeZone,
        })
        setProviderListLoading(false)
      }
    } catch (error) {
      if (error.message !== CANCELED_API_ERROR) {
        showError(error?.message || 'Something went wrong')
      }
    } finally {
      if (previousSelectedSlot?.slot && focusProviderCardRef?.current) {
        scrollContainer?.scrollTo({
          behavior: 'smooth',
          top: focusProviderCardRef.current.offsetTop - 380,
        })
        sessionStorage.setItem(sessionStorageKeys.PREVIOUS_SELECTED_SLOT, null)
      }

      const omitQueryArray = ['offset', 'partnershipCode']
      const parsedLatestQuery = _omit(parsedQuery, omitQueryArray)

      previousQueryRef.current = qs.stringify(parsedLatestQuery, {
        skipNulls: true,
      })
    }
  }

  // make an API call to get additional providers
  const getMoreProviders = () => {
    const {
      offset = '0',
      limit = PAGINATION_LIMIT.toString(),
      slotStartDate = new Date().getTime().toString(),
      latitude,
      longitude,
      state,
      gender,
      ...restQuery
    } = queryRef.current

    if (!!state && !!latitude && !!longitude) {
      const genderValues = getGenderValuesFromIds(
        gender as string[],
        formattedGroupedFilters[ProviderFilterTypes.GENDER]
      )

      fetchProviders({
        offset,
        limit,
        slotStartDate,
        latitude,
        longitude,
        state,
        ...(genderValues && { gender: genderValues }),
        ...restQuery,
      })
    }
  }

  // Load more providers
  const loadMoreProviders = () => {
    for (let i = filterIndex.current; i < SECONDARY_FILTERS.length; i++) {
      const filter = SECONDARY_FILTERS[i]
      const secondaryFilterValue = queryRef.current[filter]

      // hide show more button when all the secondary filters are removed
      let hideShowMoreButtonLimit = SECONDARY_FILTERS.length - 1

      // handling cases where there could be more values selected in multi select filters
      if (
        Array.isArray(secondaryFilterValue) &&
        secondaryFilterValue.length > 1
      ) {
        hideShowMoreButtonLimit =
          hideShowMoreButtonLimit + secondaryFilterValue.length - 1
      }

      if (i === hideShowMoreButtonLimit) {
        setShowLoadMoreButton(false)
      }

      // this case handles states with no providers

      if (!hasSecondaryFilters(queryRef.current) && filterIndex.current === 0) {
        onSetIsZeroProviderState(true)
      }

      if (secondaryFilterValue) {
        if (
          Array.isArray(secondaryFilterValue) &&
          secondaryFilterValue.length > 0
        ) {
          // Remove the last element from the array
          const updatedFilterValue = [...secondaryFilterValue]
          const removedValue = updatedFilterValue.pop()

          const removedFilterValue = getFiltersValues(
            { ...queryRef.current, [filter]: removedValue },
            [filter]
          )

          if (updatedFilterValue.length === 0) {
            queryRef.current = { ...queryRef.current, [filter]: undefined }
          } else {
            queryRef.current = {
              ...queryRef.current,
              [filter]: updatedFilterValue,
            }
          }

          removedFilters.current = [
            ...removedFilters.current,
            ...removedFilterValue,
          ]
        } else {
          const removedFilterValue = getFiltersValues(queryRef.current, [
            filter,
          ])
          queryRef.current = { ...queryRef.current, [filter]: undefined }
          removedFilters.current = [
            ...removedFilters.current,
            ...removedFilterValue,
          ]
        }

        // check if there are any more secondary filters on the queryRef
        if (!hasSecondaryFilters(queryRef.current)) {
          setShowLoadMoreButton(false)
        }

        // make an API call
        getMoreProviders()
        filterIndex.current = i
        break
      }
    }
  }

  useEffect(() => {
    onSetIsZeroProviderState(false)
    bucketIteration.current = 0
  }, [router.asPath])

  // Commented useEffect kept for reference
  // useEffect(() => {
  //   const { latitude, longitude, state } = parsedQuery

  //   if (!!state && !!latitude && !!longitude) {
  //     loadMoreProviders()
  //   }
  // }, [])

  /** The above useEffect was the one initially used. But after adding Insurance as a secondary filter,
   * we were running into a bug where if Insurance was applied in one tab and the same URL was opened in
   * another tab, the Insurance name was sometimes not being shown in the missing or in-network text.
   * This is because the insurance isn't a part of the groupedFilters initially sent from getServerSide props
   * and hence the insurance name isn't present on page load. An insurance API is called to fetch the insurance
   * details using the Insurance ID from the URL. But, if the above useEffect runs before that api is successfull,
   * we were running into stale closure issue and the Insurance name wasn't being available at time required.
   * Refer: https://thriveworks.atlassian.net/browse/THRIV-9945
   */
  useEffect(() => {
    const { latitude, longitude, state } = parsedQuery

    if (
      !!state &&
      !!latitude &&
      !!longitude &&
      ((parsedQuery?.insuranceId && insuranceProvider?.id) ||
        (!parsedQuery?.insuranceId && !insuranceProvider?.id))
    ) {
      // If the query has Insurance Id, make sure that the insuranceProvider context is also set
      // before loading providers to prevent stale closure issue.
      loadMoreProviders()
    }
  }, [parsedQuery?.insuranceId, insuranceProvider?.id])

  useEffect(() => {
    countDetailsRef.current = countDetails && {
      ...countDetails,
    }
    parentCountDetailsRef.current = countDetails && { ...countDetails }
  }, [countDetails?.slot_sum])

  useEffect(() => {
    validateNoInNetworkProviders()
  }, [waterfallResult, insuranceProvider])

  const validateNoInNetworkProviders = () => {
    const isInsuranceAppliedAndNoProviderAcceptsIt =
      insuranceProvider?.id &&
      waterfallResult.length > 0 &&
      !waterfallResult.some(
        ({ filters, results }) =>
          filters.appliedFilters.includes(insuranceProvider.name) &&
          results.length > 0
      )

    if (isInsuranceAppliedAndNoProviderAcceptsIt) {
      setNoWaterfallProviderAcceptsInsurance({
        noWaterfallProviderAcceptsInsurance: true,
        notInNWProvidersCount: waterfallResult.reduce(
          (acc, { results }) => acc + results.length,
          0
        ),
      })
    } else if (noWaterfallProviderAcceptsInsurance) {
      setNoWaterfallProviderAcceptsInsurance({
        noWaterfallProviderAcceptsInsurance: false,
        notInNWProvidersCount: 0,
      })
    }
  }

  const onClickLoadMoreProviders = () => {
    logEvent(RudderEvent.SHOW_PARTIAL_MATCHES_CLICKED)
    loadMoreProviders()
  }

  const missingDetailsInfo: MissingDetailsInfo = useMemo(() => {
    if (!hasSecondaryFilters(queryRef.current)) {
      // initial value set as 100% providers count
      let renderedProvidersCount = providerList.length

      // get the pagination details from the last bucket
      const { providerPagination } = waterfallResult[
        waterfallResult.length - 1
      ] ?? {
        providerPagination: { count: 0 },
      }

      // count the list of rendered providers
      waterfallResult.forEach(({ results: providerCardDetails }) => {
        renderedProvidersCount += providerCardDetails.length
      })

      const missingCount = providerPagination.count - renderedProvidersCount

      if (missingCount > 0) {
        return {
          isMissing: true,
          missingCount: missingCount,
        }
      }
    }

    return {
      isMissing: false,
      missingCount: 0,
    }
  }, [waterfallResult])

  const handleClearFilters = () => {
    modifyURLQuery(router, queryRef.current, true)
  }

  const renderInNetworkSection = () => {
    return (
      <div className="~flex">
        <img
          src="/icons/shield-check2.svg"
          srcSet="/icons/shield-check2.webp"
          alt="insurance in-network"
          className="~m-[6px] ~h-5 ~w-5"
        />
        <p className="~font-medium ~text-sm ~p-[5px]">
          In-network with {insuranceProvider?.name}
        </p>
      </div>
    )
  }

  const renderOutOfNetworkSection = () => {
    return (
      <div className="~flex">
        <img
          src="/icons/shield-cross.svg"
          alt="insurance out-of-network"
          className="~m-[6px] ~h-5 ~w-5"
        />
        <p className="~text-sm ~p-[5px]">
          <span className="~font-medium">
            Out-of-network with {insuranceProvider?.name}
          </span>
          , accepts self-pay
        </p>
      </div>
    )
  }

  return (
    <>
      {/* Scrollable section */}
      {waterfallResult?.map(
        ({ filters, results: providerCardDetails }, idx) => (
          <div
            key={`${providerCardDetails?.[0]?.providerDetails.id}-container`}
          >
            {!isMobileScreen && (
              <section
                className={classNames(
                  styles.normalText,
                  styles.sectionContainer
                )}
              >
                <p>
                  <span className={styles.boldText}>
                    {`${providerCardDetails.length} available provider${
                      providerCardDetails.length > 1 ? 's' : ''
                    }`}
                  </span>
                  {` match${providerCardDetails.length > 1 ? '' : 'es'} ${
                    filters.appliedFilters.length <= 1 ? 'one' : 'some'
                  } of your filters`}
                  {filters.appliedFilters.length === 0 ? '.' : ':'}{' '}
                  <span>{filters.appliedFilters.join(' \u25cf ')}</span>
                </p>
                <p>
                  {' '}
                  <span className={styles.boldText}>Missing: </span>
                  <span className={styles.strikethroughText}>
                    {filters.removedFilters.join(' \u25cf ')}
                  </span>
                </p>
              </section>
            )}

            {isMobileScreen && (
              <section
                className={classNames(
                  styles.normalText__mobile,
                  styles.sectionContainer__mobile
                )}
              >
                <p>
                  <span className={styles.boldText__mobile}>
                    {`${providerCardDetails.length} available provider${
                      providerCardDetails.length > 1 ? 's' : ''
                    }`}
                  </span>
                  {` match${providerCardDetails.length > 1 ? '' : 'es'} ${
                    filters.appliedFilters.length <= 1 ? 'one' : 'some'
                  } of your filters`}
                  {filters.appliedFilters.length === 0 ? '.' : ':'}{' '}
                  <span>{filters.appliedFilters.join(' \u25cf ')}</span>
                </p>
                <p>
                  <span className={styles.boldText__mobile}>Missing: </span>
                  <span className={styles.strikethroughText__mobile}>
                    {filters.removedFilters.join(' \u25cf ')}
                  </span>
                </p>
              </section>
            )}
            {providerCardDetails?.map((providerCardDetails) => {
              const { providerDetails } = providerCardDetails
              return (
                <div
                  ref={
                    providerDetails.id === previousSelectedSlot?.provider?.id
                      ? focusProviderCardRef
                      : null
                  }
                  key={`${providerDetails.id}-list-card`}
                  className={styles.ProviderCard_container}
                >
                  <ListCard
                    groupedFilters={formattedGroupedFilters}
                    showDistance={
                      inPersonFilterSelected || isSupportOrThirdPartyUser
                    }
                    slotLocation={SLOT_LOCATION_MAP.indirect_match}
                    providerCardDetails={providerCardDetails}
                    renderCustomInsuranceSection={
                      filters.removedFilters.includes(insuranceProvider?.name)
                        ? renderOutOfNetworkSection
                        : filters.appliedFilters.includes(
                            insuranceProvider?.name
                          )
                        ? renderInNetworkSection
                        : undefined
                    }
                  />
                </div>
              )
            })}
            {idx < waterfallResult.length - 1 && waterfallResult && (
              <div
                className={classNames(
                  commonStyles.waterfall_bucket_divider_container,
                  {
                    [commonStyles.waterfall_bucket_divider_container__mobile]: isMobileScreen,
                  }
                )}
              >
                <img
                  src={
                    isMobileScreen
                      ? '/images/squiggle.svg'
                      : '/images/waterfall-bucket-divider.svg'
                  }
                  alt="Waterfall bucket divider"
                  className={commonStyles.waterfall_bucket_divider}
                />
              </div>
            )}
          </div>
        )
      )}

      {(providerListLoading || showLoadMoreButton) && (
        <div className={styles.LoadMoreButtonContainer}>
          <img
            src="/images/squiggle.svg"
            alt="Load more button left squiggle"
            className={styles.button_squiggle}
          />
          <Button
            disabled={providerListLoading}
            onClick={onClickLoadMoreProviders}
            variant="secondary"
            theme="v2"
            size="normal"
            className={styles.load_more_btn}
          >
            {providerListLoading ? (
              <Spinner animation="border" />
            ) : (
              'Find more partial matches'
            )}
          </Button>
          <img
            src="/images/squiggle.svg"
            alt="Load more button right squiggle"
            className={styles.button_squiggle}
          />
        </div>
      )}

      {missingDetailsInfo.isMissing && (
        <div className={styles.NoMoreProvidersContainer}>
          <p className={styles.providerCountMessage}>
            There are {missingDetailsInfo.missingCount} more available
            providers.
            <button
              className={styles.clearFiltersButton}
              onClick={handleClearFilters}
            >
              Clear your filters to see them
            </button>
          </p>
        </div>
      )}

      {!hasSecondaryFilters(queryRef.current) &&
        !providerListLoading &&
        !showLoadMoreButton &&
        !missingDetailsInfo.isMissing && (
          <div className={styles.NoMoreProvidersContainer}>
            <p>
              There are no more available matches. Check back soon or adjust
              your filters further.
            </p>
          </div>
        )}
    </>
  )
}

export default ProvidersListWaterfall
